<template>
    <validation-observer ref="emailFormValidator" as="div">
        <form-card
            :title="`Email to ${name}`"
            :initial-collapsed="collapsed"
            @toggle="collapsed = $event"
            :use-h6="true"
            card-class="mt-4 mb-2"
            body-class="p-3"
        >
            <b-row class="mt-2">
                <b-col>
                    <validation-provider name="to" rules="required|email" v-slot="{ errors }">
                        <form-input
                            floatingLabel="To"
                            v-model="to"
                            :rightButton="{
                                title: 'CC',
                                text: 'CC',
                                variant: 'outline-primary',
                                toggle: true,
                            }"
                            @handle-right-button-click="toggleCC"
                            :rightButton2="{
                                title: 'BCC',
                                text: 'BCC',
                                variant: 'outline-primary',
                                toggle: true,
                            }"
                            @handle-right-button-2-click="toggleBCC"
                            :error="errors[0]"
                            @input="updateField('to', $event)"
                        />
                    </validation-provider>
                </b-col>
            </b-row>

            <b-row v-if="isCC" class="no-gutters">
                <b-col md="3">
                    <form-select
                        floatingLabel="CC Mode"
                        :options="ccModeOptions"
                        v-model="ccMode"
                        @change="onCCModeChange"
                    />
                </b-col>
                <b-col md="9">
                    <validation-provider name="cc" rules="email" v-slot="{ errors }">
                        <form-input
                            floatingLabel="CC"
                            v-if="ccMode === 'OTHER'"
                            class="cc-email-input"
                            v-model="cc"
                            :error="errors[0]"
                            @input="updateField('cc', $event)"
                        />
                    </validation-provider>
                    <form-select
                        floatingLabel="CC"
                        :options="teamMemberOptions"
                        v-model="selectedCC"
                        v-if="ccMode === 'TEAM_MEMBER'"
                    />
                    <form-select
                        floatingLabel="CC"
                        :options="ownerOptions"
                        v-model="selectedCC"
                        v-if="ccMode === 'OWNER'"
                    />
                </b-col>
            </b-row>

            <b-row v-if="isBCC">
                <b-col>
                    <validation-provider name="bcc" rules="email" v-slot="{ errors }">
                        <form-input
                            floatingLabel="BCC"
                            v-model="bcc"
                            :error="errors[0]"
                            @input="updateField('bcc', $event)"
                        />
                    </validation-provider>
                </b-col>
            </b-row>

            <b-row>
                <b-col>
                    <validation-provider name="subject" rules="required" v-slot="{ errors }">
                        <form-input
                            floatingLabel="Subject"
                            v-model="subject"
                            :error="errors[0]"
                            @input="updateField('subject', $event)"
                        />
                    </validation-provider>
                </b-col>
            </b-row>

            <b-row class="no-gutters">
                <b-col md="9">
                    <form-select
                        floatingLabel="Email Response"
                        placeholder="Insert email template response"
                        :options="emailResponseOptions"
                        v-model="selectedEmailResponseOption"
                    />
                </b-col>
                <b-col md="3" class="pb-3 pl-md-2 pb-md-0">
                    <b-button
                        class="right-button"
                        variant="primary"
                        @click="applySelectedResponse"
                        :disabled="isNil(selectedEmailResponseOption)"
                    >
                        Apply
                    </b-button>
                </b-col>
            </b-row>

            <validation-provider name="message" rules="required" v-slot="{ errors }">
                <lx-editor
                    class="lx-editor"
                    v-model="message"
                    :error="errors[0]"
                    onlyContentEditable
                    @input="updateField('message', $event)"
                />
            </validation-provider>
        </form-card>
    </validation-observer>
</template>

<script>
    import * as DOMPurify from 'dompurify';
    import isNil from 'lodash/isNil';
    import FormInput from '../generic/form-input/form-input.vue';
    import FormSelect from '../generic/form-select/form-select.vue';
    import LxEditor from '../generic/lx-editor/lx-editor.vue';
    import FormCard from '../generic/form-card/form-card.vue';
    import API from '../../api';
    import { OWNER_STATEMENT_APPLY_EMAIL_RESPONSE } from '../../endpoints';
    import { formatResponseError } from '../../utils/api';

    export default {
        name: 'OwnerStatementEmailForm',
        components: {
            FormInput,
            FormSelect,
            LxEditor,
            FormCard,
        },
        props: {
            initialTo: {
                type: String,
                default: '',
            },
            initialSubject: {
                type: String,
                default: '',
            },
            initialMessage: {
                type: String,
                default: '',
            },
            initialCc: {
                type: String,
                default: '',
            },
            initialBcc: {
                type: String,
                default: '',
            },
            initialCcMode: {
                type: String,
                default: 'OTHER',
            },
            initialCcId: {
                type: Number,
                default: null,
            },
            initialSelectedEmailResponse: {
                type: Number,
                default: null,
            },
            ownerId: {
                type: Number,
                default: null,
            },
            ownerStatementId: {
                type: Number,
                default: null,
            },
            teamMemberOptions: {
                type: Array,
                default: () => [],
            },
            ownerOptions: {
                type: Array,
                default: () => [],
            },
            emailResponseOptions: {
                type: Array,
                default: () => [],
            },
            ownerName: {
                type: String,
                default: 'Owner',
            },
            autoApplyResponse: {
                type: Boolean,
                default: true,
            },
            isUsingUnlayer: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                to: this.initialTo,
                id: this.ownerId,
                name: this.ownerName,
                subject: this.initialSubject,
                message: this.initialMessage,
                cc: this.initialCc,
                bcc: this.initialBcc,
                isCC: !!this.initialCc || !!this.initialCcId,
                isBCC: !!this.initialBcc,
                ccMode: this.initialCcMode,
                selectedCC: this.initialCcId,
                selectedEmailResponseOption: this.initialSelectedEmailResponse,
                collapsed: false,
                formInvalidState: true,
            };
        },
        mounted() {
            if (this.autoApplyResponse && this.emailResponseOptions.length > 0 && !this.message) {
                this.loadDefaultEmailResponse();
            } else if (this.initialMessage) {
                this.updateField('to', this.initialTo);
                this.updateField('subject', this.initialSubject);
                this.updateField('message', this.initialMessage);
                if (this.cc) this.updateField('cc', this.cc);
                if (this.bcc) this.updateField('bcc', this.bcc);
                this.selectedEmailResponseOption = this.initialSelectedEmailResponse || this.defaultEmailResponseId;
            }
            this.validateFormAndEmit();
        },
        computed: {
            isNil() {
                return isNil;
            },
            ccModeOptions() {
                const base = [{ value: 'OTHER', text: 'Other' }];
                if (this.teamMemberOptions.length > 0) base.push({ value: 'TEAM_MEMBER', text: 'Team Member' });
                if (this.ownerOptions.length > 0) base.push({ value: 'OWNER', text: 'Owner' });
                return base;
            },
            defaultEmailResponseId() {
                const defaultResponse = this.emailResponseOptions.find((option) => option.isDefault);
                return defaultResponse ? defaultResponse.value : null;
            },
        },
        methods: {
            async loadDefaultEmailResponse() {
                this.updateField('to', this.initialTo);
                const responseId = this.initialSelectedEmailResponse || this.defaultEmailResponseId;
                if (responseId && !this.message) {
                    this.selectedEmailResponseOption = responseId;
                    await this.applySelectedResponse();
                }
            },
            async applySelectedResponse() {
                if (isNil(this.selectedEmailResponseOption)) return;
                try {
                    const response = await API.post(OWNER_STATEMENT_APPLY_EMAIL_RESPONSE(), {
                        statement_ids: [this.ownerStatementId],
                        email_response_id: this.selectedEmailResponseOption,
                        subject_text: this.subject,
                        is_unlayer: this.isUsingUnlayer,
                    });
                    if (response.data && response.data.results) {
                        this.to = response.data.results[0].owner_email;
                        this.id = response.data.results[0].owner_id;
                        this.name = response.data.results[0].owner_name;
                        this.subject = this.cleanMessage(response.data.results[0].subject);
                        this.message = this.cleanMessage(response.data.results[0].message);

                        this.updateField('to', this.to);
                        this.updateField('name', this.name);
                        this.updateField('subject', this.subject);
                        this.updateField('message', this.message);
                    }
                    this.validateFormAndEmit();
                } catch (err) {
                    const { generic } = formatResponseError(err);
                    this.errorToast({ text: generic || 'Could not apply email response option.' });
                }
            },
            updateField(field, value) {
                if (this.$refs.emailFormValidator) {
                    this.$refs.emailFormValidator.setFieldValue(field, value);
                    this.validateFormAndEmit();
                }
            },
            async validateFormAndEmit() {
                if (this.$refs.emailFormValidator) {
                    const { valid } = await this.$refs.emailFormValidator.validate();
                    const isInvalid = !valid;
                    this.formInvalidState = isInvalid;
                    this.$emit('validation-changed', isInvalid);
                }
            },
            async isFormInvalid() {
                if (this.$refs.emailFormValidator) {
                    const { valid } = await this.$refs.emailFormValidator.validate();
                    const isInvalid = !valid;
                    this.formInvalidState = isInvalid;
                    return isInvalid;
                }
                return true;
            },
            onCCModeChange() {
                if (this.ccMode === 'TEAM_MEMBER' && this.teamMemberOptions.length > 0) {
                    this.selectedCC = this.teamMemberOptions[0].value;
                } else if (this.ccMode === 'OWNER' && this.ownerOptions.length > 0) {
                    this.selectedCC = this.ownerOptions[0].value;
                }
            },
            toggleCC(status) {
                this.isCC = status;
                if (!status) {
                    this.cc = '';
                    this.selectedCC = null;
                    this.ccMode = 'OTHER';
                }
            },
            toggleBCC(status) {
                this.isBCC = status;
                if (!status) {
                    this.bcc = '';
                }
            },
            getEmailData() {
                return {
                    to: this.to,
                    cc: this.cc,
                    cc_id: this.selectedCC,
                    cc_mode: this.ccMode,
                    bcc: this.bcc,
                    subject: this.subject,
                    email_body: this.cleanMessage(this.message),
                    owner_id: this.id,
                };
            },
            cleanMessage(text) {
                const config = {
                    ADD_TAGS: ['center'],
                    ADD_ATTR: [
                        'href',
                        'name',
                        'target',
                        'src',
                        'srcset',
                        'alt',
                        'title',
                        'width',
                        'height',
                        'loading',
                        'style',
                        'colspan',
                        'align',
                    ],
                };
                return DOMPurify.sanitize(text || '', config);
            },
            toggleCollapse() {
                this.collapsed = !this.collapsed;
            },
            clearMessage() {
                this.message = '';
                this.subject = '';
                this.cc = '';
                this.bcc = '';
                this.selectedCC = null;
                this.ccMode = 'OTHER';
                this.isCC = false;
                this.isBCC = false;
                this.selectedEmailResponseOption = null;
                this.updateField('message', '');
                this.updateField('subject', '');
                this.updateField('cc', '');
                this.updateField('bcc', '');
                this.validateFormAndEmit();
            },
            getSelectedEmailResponseOption() {
                return this.selectedEmailResponseOption;
            },
            getValidationState() {
                return this.formInvalidState;
            },
        },
    };
</script>

<style lang="scss" scoped>
    .right-button {
        height: 2.75rem !important;
        width: 100% !important;
        font-weight: 500 !important;
        font-size: 0.95rem !important;
        padding: 0.6rem 1rem !important;
    }
</style>
